//import { HostListener } from '@angular/core';
//import { MatSnackBar } from "@angular/material/snack-bar";
import { environment } from '../environments/environment';
import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { AuthenticationResult, InteractionStatus, PopupRequest, RedirectRequest, EventMessage, EventType, InteractionType, AccountInfo, SsoSilentRequest } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { IdTokenClaims, PromptValue } from '@azure/msal-common';

type IdTokenClaimsWithPolicyId = IdTokenClaims & {
    acr?: string,
    tfp?: string,
};
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Angular 16 B2C Sample - MSAL Angular v3';
  isIframe = false;
  loginDisplay = false;
  private readonly _destroying$ = new Subject<void>();

  constructor(
      @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
      private authService: MsalService,
      private msalBroadcastService: MsalBroadcastService
  ) { }

  ngOnInit(): void {
      this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal
      this.setLoginDisplay();

      this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
      this.msalBroadcastService.msalSubject$
          .pipe(
              filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
          )
          .subscribe((result: EventMessage) => {
              //console.log("GetAllAccounts() 1 : "+this.authService.instance.getAllAccounts().length);
              if (this.authService.instance.getAllAccounts().length === 0) {
                  window.location.pathname = "/";
              } else {
                  this.setLoginDisplay();
              }
          });


      this.msalBroadcastService.inProgress$
          .pipe(
              filter((status: InteractionStatus) => status === InteractionStatus.None),
              takeUntil(this._destroying$)
          )
          .subscribe(() => {
              this.setLoginDisplay();
              this.checkAndSetActiveAccount();
          })

      this.msalBroadcastService.msalSubject$
          .pipe(
              filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS 
                  || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS 
                  || msg.eventType === EventType.SSO_SILENT_SUCCESS),
              takeUntil(this._destroying$)
          )
          .subscribe((result: EventMessage) => {

              let payload = result.payload as AuthenticationResult;
              let idtoken = payload.idTokenClaims as IdTokenClaimsWithPolicyId;

              if (idtoken.acr === environment.b2cPolicies.names.signUpSignIn || idtoken.tfp === environment.b2cPolicies.names.signUpSignIn) {
                  this.authService.instance.setActiveAccount(payload.account);
              }
              
              /**
               * For the purpose of setting an active account for UI update, we want to consider only the auth response resulting
               * from SUSI flow. "acr" claim in the id token tells us the policy (NOTE: newer policies may use the "tfp" claim instead).
               * To learn more about B2C tokens, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
               */
              if (idtoken.acr === environment.b2cPolicies.names.editProfile || idtoken.tfp === environment.b2cPolicies.names.editProfile) {
                  //console.log("ARE YOU CALLING EDIT PROFILE?");       
                  // retrieve the account from initial sing-in to the app
                  const originalSignInAccount = this.authService.instance.getAllAccounts()
                      .find((account: AccountInfo) =>
                              account.idTokenClaims?.oid === idtoken.oid
                              && account.idTokenClaims?.sub === idtoken.sub
                              && ((account.idTokenClaims as IdTokenClaimsWithPolicyId).acr === environment.b2cPolicies.names.signUpSignIn
                                  || (account.idTokenClaims as IdTokenClaimsWithPolicyId).tfp === environment.b2cPolicies.names.signUpSignIn)
                          );

                  //console.log("Original sign in account: "+originalSignInAccount);
                  let signUpSignInFlowRequest: SsoSilentRequest = {
                      authority: environment.b2cPolicies.authorities.signUpSignIn.authority,
                      account: originalSignInAccount
                  };

                  // silently login again with the signUpSignIn policy
                  this.authService.ssoSilent(signUpSignInFlowRequest);
              }

              /**
               * Below we are checking if the user is returning from the reset password flow.
               * If so, we will ask the user to reauthenticate with their new password.
               * If you do not want this behavior and prefer your users to stay signed in instead,
               * you can replace the code below with the same pattern used for handling the return from
               * profile edit flow (see above ln. 74-92).
               */
             if (idtoken.acr === environment.b2cPolicies.names.resetPassword || idtoken.tfp === environment.b2cPolicies.names.resetPassword) {
                  //console.log("ARE YOU RESETTING PWD??");
                  let signUpSignInFlowRequest: RedirectRequest | PopupRequest  = {
                      authority: environment.b2cPolicies.authorities.signUpSignIn.authority,
                      scopes: [...environment.apiConfig.scopes],
                      prompt: PromptValue.LOGIN // force user to reauthenticate with their new password
                  };

                  this.login(signUpSignInFlowRequest);
              }

              return result;
          });

      this.msalBroadcastService.msalSubject$
          .pipe(
              filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE || msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
              takeUntil(this._destroying$)
          )
          .subscribe((result: EventMessage) => {
              // Check for forgot password error
              // Learn more about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
              if (result.error && result.error.message.indexOf('AADB2C90118') > -1) {
                  let resetPasswordFlowRequest: RedirectRequest | PopupRequest  = {
                      authority: environment.b2cPolicies.authorities.resetPassword.authority,
                      scopes: [],
                  };
          
                  this.login(resetPasswordFlowRequest);
              };
          });
  }

  setLoginDisplay() {
      //console.log("GetAllAccounts() called in setLoginDisplay() in app component: "+this.authService.instance.getAllAccounts().length);
      this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount() {
      /**
       * If no active account set but there are accounts signed in, sets first account to active account
       * To use active account set here, subscribe to inProgress$ first in your component
       * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
       */
      let activeAccount = this.authService.instance.getActiveAccount();

      //console.log("CHECK AND SET ACTIVE ACCOUNT: ");
      if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
          //console.log("number: "+this.authService.instance.getAllAccounts().length);
          let accounts = this.authService.instance.getAllAccounts();
          this.authService.instance.setActiveAccount(accounts[0]);
      }
  }

  loginRedirect() {
      if (this.msalGuardConfig.authRequest) {
          this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
      } else {
          this.authService.loginRedirect();
      }
  }

  login(userFlowRequest?: RedirectRequest | PopupRequest) {
      if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
          if (this.msalGuardConfig.authRequest) {
              this.authService.loginPopup({ ...this.msalGuardConfig.authRequest, ...userFlowRequest } as PopupRequest)
                  .subscribe((response: AuthenticationResult) => {
                      this.authService.instance.setActiveAccount(response.account);
                  });
          } else {
              this.authService.loginPopup(userFlowRequest)
                  .subscribe((response: AuthenticationResult) => {
                      this.authService.instance.setActiveAccount(response.account);
                  });
          }
      } else {
          if (this.msalGuardConfig.authRequest) {
              this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest, ...userFlowRequest } as RedirectRequest);
          } else {
              this.authService.loginRedirect(userFlowRequest);
          }
      }
  }

  logout() {
      if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
          this.authService.logoutPopup({
              mainWindowRedirectUri: "/"
          });
      } else {
          this.authService.logoutRedirect();
      }
  }

  editProfile() {
      let editProfileFlowRequest: RedirectRequest | PopupRequest  = {
          authority: environment.b2cPolicies.authorities.editProfile.authority,
          scopes: [],
      };

      this.login(editProfileFlowRequest);
  }

  ngOnDestroy(): void {
      this._destroying$.next(undefined);
      this._destroying$.complete();
  }
}

